import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import {
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  Repeat,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { errorMessage } from '@/api/errors'
import { useCourseSessionMutations, useCourseSessions, useCurrentPermissions } from '@/api/queries'
import type { CourseSession } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { AttendanceSheet } from '@/features/courses/attendance-sheet'
import { SessionDialog } from '@/features/courses/session-dialog'
import { WeekGrid } from '@/features/courses/week-grid'
import { addDays, startOfWeek } from '@/features/courses/week'
import { plural } from '@/lib/format'
import { CourseCatalogPanel } from '@/features/courses/catalog-panel'
import { RecurrencePanel } from '@/features/courses/recurrence-panel'

/**
 * Il palinsesto — COR-06.
 *
 * ## Una settimana per volta
 *
 * Il server accetta finestre fino a 93 giorni, ma la domanda che si fa davanti a
 * un palinsesto è settimanale: «questa settimana com'è messa». Chiedere il
 * trimestre per mostrarne un settimo sarebbe scaricare sul browser un lavoro che
 * nessuno guarda.
 *
 * ## I gesti sono tre, e sono quelli che si fanno in piedi
 *
 * Aprire una casella vuota crea una lezione lì; trascinarne una la sposta;
 * aprirne una mostra il foglio presenze. Tutto il resto — regole ricorrenti,
 * chiusure, sale — vive nelle rotte che il pannello ancora non espone, ed è
 * dichiarato in fondo alla pagina invece di essere lasciato cercare.
 *
 * ## Chi legge e chi scrive
 *
 * Il trainer ha `course:read` e basta: per lui la griglia è una griglia, senza
 * caselle cliccabili e senza trascinamento. Nascondere ciò che il server
 * rifiuterebbe è meglio che offrire un gesto che risponde `403`.
 */
export const Route = createFileRoute('/_app/palinsesto/')({
  component: SchedulePage,
})

function SchedulePage() {
  const permissions = useCurrentPermissions()
  const canWrite = permissions.has('course:write')

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()))
  const [openSession, setOpenSession] = useState<CourseSession | null>(null)
  const [creatingAt, setCreatingAt] = useState<Date | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  const range = useMemo(
    () => ({
      from: weekStart.toISOString(),
      to: addDays(weekStart, 7).toISOString(),
    }),
    [weekStart],
  )

  const sessions = useCourseSessions(range)
  const mutations = useCourseSessionMutations()

  const rows = sessions.data?.data ?? []
  const scheduled = rows.filter((row) => row.status === 'scheduled')
  const booked = scheduled.reduce((sum, row) => sum + row.bookedCount, 0)
  const waiting = scheduled.reduce((sum, row) => sum + row.waitlistCount, 0)

  // La lezione aperta viene riletta dall'elenco: dopo un check-in i conteggi
  // cambiano, e un dialog che tiene la copia di quando è stato aperto mostrerebbe
  // «12/20» mentre la griglia dietro dice «13/20».
  const current =
    openSession === null ? null : (rows.find((row) => row.id === openSession.id) ?? openSession)

  const label = `${weekStart.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })} – ${addDays(
    weekStart,
    6,
  ).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}`

  async function moveSession(session: CourseSession, startsAt: Date) {
    setMoveError(null)
    try {
      await mutations.update.mutateAsync({
        id: session.id,
        input: { startsAt: startsAt.toISOString() },
      })
    } catch (cause) {
      setMoveError(errorMessage(cause))
    }
  }

  return (
    <>
      <PageHeader
        title="Palinsesto"
        description="La settimana dei corsi: chi tiene cosa, dove, e quanti posti restano."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="neutral">{plural(scheduled.length, 'lezione', 'lezioni')}</Badge>
            <Badge variant="neutral">{plural(booked, 'prenotato', 'prenotati')}</Badge>
            {waiting > 0 ? <Badge variant="warning">{waiting} in coda</Badge> : null}
          </div>
        }
      />

      {/*
        Due schede e non due pagine: il **corso** è il modello e la **lezione** l'occorrenza, e
        chi compila il palinsesto della stagione passa dall'uno all'altra di continuo. Prima del
        20 agosto la prima non esisteva affatto — `POST /organization/courses` era in produzione
        e nessuno la chiamava, quindi una palestra non poteva creare un corso.
      */}
      <Tabs variant="pill" defaultValue="week">
        <TabsList>
          <TabsTrigger value="week">
            <CalendarRange /> Settimana
          </TabsTrigger>
          <TabsTrigger value="catalog">
            <Dumbbell /> Corsi e sale
          </TabsTrigger>
          <TabsTrigger value="recurrence">
            <Repeat /> Ricorrenze e chiusure
          </TabsTrigger>
        </TabsList>

        <TabsContent value="recurrence">
          <RecurrencePanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="catalog">
          <CourseCatalogPanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="week">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              aria-label="Settimana precedente"
              onClick={() => setWeekStart((current) => addDays(current, -7))}
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWeekStart(startOfWeek(new Date()))}
            >
              Oggi
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Settimana successiva"
              onClick={() => setWeekStart((current) => addDays(current, 7))}
            >
              <ChevronRight />
            </Button>
            <span className="text-muted-foreground text-sm">{label}</span>
            {sessions.isFetching ? (
              <span className="text-muted-foreground text-xs">aggiorno…</span>
            ) : null}
          </div>

          {moveError !== null ? (
            <Alert variant="danger" className="mb-3" title="La lezione non è stata spostata">
              {moveError}
            </Alert>
          ) : null}

          {sessions.isError ? (
            <Alert variant="danger" title="Il palinsesto non si è caricato">
              {errorMessage(sessions.error)}
            </Alert>
          ) : sessions.isPending ? (
            <Skeleton className="h-96 w-full" />
          ) : rows.length === 0 && !canWrite ? (
            <EmptyState
              icon={<CalendarDays />}
              title="Nessuna lezione questa settimana"
              description="Il palinsesto lo compila chi ha il permesso di scrivere sui corsi."
            />
          ) : (
            <WeekGrid
              weekStart={weekStart}
              sessions={rows}
              canWrite={canWrite}
              onOpenSession={setOpenSession}
              onCreateAt={setCreatingAt}
              onMoveSession={(session, startsAt) => void moveSession(session, startsAt)}
            />
          )}

          <p className="text-muted-foreground mt-3 text-xs">
            {canWrite
              ? 'Tocca una casella vuota per creare una lezione, trascinala per spostarla, aprila per il foglio presenze.'
              : 'Sola lettura: servono i permessi sui corsi per modificare il palinsesto.'}{' '}
            Le regole ricorrenti e le chiusure esistono nell'API e non ancora qui: una lezione
            generata da una regola si può spostare, e da quel momento la regola non la riscrive più.
          </p>

          <AttendanceSheet
            session={current}
            canWrite={canWrite}
            onClose={() => setOpenSession(null)}
          />
        </TabsContent>
      </Tabs>

      <SessionDialog startsAt={creatingAt} onClose={() => setCreatingAt(null)} />
    </>
  )
}
